import {
  FormButtonType,
  IDocument,
  IFormButton,
} from 'app/services/api5-service/api.interface';
import { BehaviorSubject } from 'rxjs';

import { ThemePalette } from '@ispui/ng-button';

import { DocHelper } from 'utils/dochelper';

import { IFormButtonUi } from './model/form-button.interface';

/** Maximum counter number that can be shown */
const MAXIMUM_BUTTON_COUNT = 9;

/** List of button types that can be used to submit the form */
const SUBMIT_BUTTON_TYPE_LIST: FormButtonType[] = [
  FormButtonType.Ok,
  FormButtonType.Next,
  FormButtonType.Submit,
  FormButtonType.Blank,
];

/**
 * Get count for button icon
 *
 * @param count
 */
function getCount(count?: string): string | undefined {
  if (Number(count) > MAXIMUM_BUTTON_COUNT) {
    return `${MAXIMUM_BUTTON_COUNT}+`;
  }
  return count;
}

/**
 * Dragon theme BE buttons override
 *
 * @HACK this is hardcode! BE should send us required metadata in order to set up buttons!
 * @param button - button metadata
 * @param originalButton - BE button metadata
 */
function appendButtonOverride(
  button: IFormButtonUi,
  originalButton: IFormButton,
): void {
  // delete buttons
  if (
    (button.name === 'clearbasket' || button.name === 'delete') &&
    !button.icon
  ) {
    button.theme = 'inline-red';
    button.label = '';
    button.icon = 't-busket';
    return;
  }

  // settings buttons
  if (button.name === 'settings') {
    button.label = '';
    button.icon = 'p-attr';
    button.theme = 'secondary';
    return;
  }

  // @HACK only 'cancel' button displayed as a inline-blue
  if (
    (button.type === FormButtonType.Cancel || button.name === 'cancel') &&
    !originalButton.$theme
  ) {
    button.theme = 'inline-blue';
    return;
  }
}

/**
 * Is it a button to submit the form or not
 *
 * @param button - button meta
 */
function isSubmitButton(button: IFormButton): boolean {
  return SUBMIT_BUTTON_TYPE_LIST.includes(button.$type);
}

/**
 * Is it a button to submit the form or not
 *
 * @param button - button meta
 */
export function isSubmitButtonUi(button: IFormButtonUi): boolean {
  return SUBMIT_BUTTON_TYPE_LIST.includes(button.type);
}

/**
 * Get button title
 *
 * @param doc - doc instance
 * @param button - button meta
 */
function getButtonLabel(doc: IDocument, button: IFormButton): string {
  return DocHelper.getMessage(`msg_${button.$name}`, doc);
}

/**
 * Get hint for button
 *
 * @param doc - doc instance
 * @param name - button name
 * @param forDisabledState - is hint for button disabled state
 */
function getHint(
  doc: IDocument,
  name: string,
  forDisabledState: boolean,
): string {
  const suffix = forDisabledState ? '_disabled' : '';
  return DocHelper.getMessage(`hint_${name}${suffix}`, doc);
}

/**
 * Get button theme
 *
 * @param button - button metadata
 */
function getButtonTheme(button: IFormButton): ThemePalette {
  if (button.$theme) {
    return button.$theme;
  }

  if (button.$color && ['yellow'].includes(button.$color)) {
    return 'accent';
  }

  if (!isSubmitButton(button)) {
    return 'secondary';
  }

  return 'primary';
}

/**
 * Create form button object for button component
 *
 * @param options - options for button
 */
export function createButton(
  options: Partial<
    Omit<IFormButtonUi, 'disabledSubject' | 'id' | 'preloaderSubject'>
  > & { disabled?: boolean; type: IFormButtonUi['type'] },
): IFormButtonUi {
  return {
    id: `${Date.now()}${Math.random()}`,
    preloaderSubject: new BehaviorSubject(false),
    disabledSubject: new BehaviorSubject<boolean>(options.disabled === true),
    name: options.name || '',
    label: options.label,
    type: options.type,
    blocking: options.blocking,
    icon: options.icon,
    offset: options.offset,
    hint: options.hint,
    func: options.func,
    keepform: options.keepform,
    key: options.key,
    theme: options.theme,
    size: options.size,
    fullWidth: options.fullWidth,
    originalDisabled: Boolean(options.originalDisabled),
    confirm: options.confirm,
    counter: options.counter,
  };
}

/**
 * Get button list for dynamic-form footer or for buttons in list field row
 *
 * @param doc - doc instance
 * @param buttons - buttons metadata
 * @param showHints - is hints can be shown
 */
export function getButtonList(
  doc: IDocument,
  buttons: IFormButton[],
  showHints: boolean,
): IFormButtonUi[] {
  return buttons.map(b => {
    const button: IFormButtonUi = createButton({
      name: b.$name,
      label: getButtonLabel(doc, b),
      icon: b.$icon,
      theme: getButtonTheme(b),
      fullWidth: b.$fullwidth === 'yes',
      disabled: b.$disabled === 'yes',
      offset: b.$offset === 'yes',
      keepform: b.$keepform,
      type: b.$type,
      func: b.$func,
      blocking: b.$blocking,
      key: b.$key,
      size: b.$size,
      originalDisabled: b.$disabled === 'yes',
      counter: getCount(b.count_icon?.$),
      confirm:
        b.$confirm === 'yes'
          ? {
              content: DocHelper.getMessage(`msg_${b.$name}_confirm`, doc),
              title: DocHelper.getMessage(`msg_${b.$name}_confirm_title`, doc),
              ok: DocHelper.getMessage(`msg_${b.$name}_confirm_ok`, doc),
              cancel: DocHelper.getMessage(
                `msg_${b.$name}_confirm_cancel`,
                doc,
              ),
            }
          : undefined,
    });

    appendButtonOverride(button, b);

    if (showHints) {
      button.hint = getHint(doc, b.$name, false);
      button.hintDisabled = getHint(doc, b.$name, true);
    }

    return button;
  });
}
